import { Close } from '@mui/icons-material';
import {
  IconButton,
  InputAdornment,
  InputBase,
  type SxProps,
  type Theme,
  styled,
  useTheme,
} from '@mui/material';
import { SearchNormal1 } from 'iconsax-react';
import { type FC, useRef, useState } from 'react';

import { hasStringValue, hasValue } from '@core/utils/equality';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: '0.5rem',
  border: `1px solid ${theme.palette.SlabGray['500']}`,
  backgroundColor: theme.palette.SlabGray[0],
  '&:hover': {
    backgroundColor: theme.palette.SlabGray['50'],
  },
}));

const SearchIconWrapper = styled('div')(() => ({
  padding: '0.875rem',
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: theme.palette.SlabGray['900'],
  width: '100%',
  '& .MuiInputBase-input': {
    padding: '1rem',
    paddingLeft: '3rem',
  },
}));

type Props = {
  initialValue?: string;
  value?: string;
  onChange: (value: string) => void;
  sx?: SxProps<Theme>;
  placeholder?: string;
  disabled?: boolean;
};

const InputSearch: FC<Props> = ({
  disabled,
  initialValue,
  value,
  onChange,
  sx,
  placeholder = 'Search',
}) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uncontrolledValue, setUncontrolledValue] = useState(initialValue ?? '');
  const currentValue = hasValue(value) ? value : uncontrolledValue;

  const change = (next: string): void => {
    setUncontrolledValue(next);
    onChange(next);
  };

  return (
    <Search
      sx={{
        ...sx,
      }}
    >
      <SearchIconWrapper>
        <SearchNormal1
          variant='TwoTone'
          color={theme.palette.SlabIndigo['700']}
          style={{
            width: '1.25rem',
            height: '1.25rem',
          }}
        />
      </SearchIconWrapper>
      <StyledInputBase
        inputRef={inputRef}
        placeholder={placeholder}
        value={currentValue}
        disabled={disabled}
        onChange={(e): void => change(e.target.value)}
        endAdornment={
          hasStringValue(currentValue) && !disabled ? (
            <InputAdornment position='end' sx={{ marginRight: '0.5rem' }}>
              <IconButton
                size='small'
                aria-label='Clear search'
                onClick={(e): void => {
                  e.stopPropagation();
                  change('');
                  inputRef.current?.focus();
                }}
              >
                <Close sx={{ fontSize: 22, color: theme.palette.SlabGray[600] }} />
              </IconButton>
            </InputAdornment>
          ) : undefined
        }
      />
    </Search>
  );
};

export default InputSearch;
